import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import LanguageIcon from '@mui/icons-material/Language';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { colorTokens } from '@eruvaka/web-pondlogs-base';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { fetchProductManuals } from '../api/releaseNotesApi';
const MANUAL_META = {
    WEB: { Icon: LanguageIcon, color: colorTokens.primary, platformLabel: 'Web' },
    MOBILE: { Icon: SmartphoneIcon, color: colorTokens.secondary, platformLabel: 'Android & iOS' },
};
/* ── PDF Viewer Dialog ─────────────────────────────── */
const PdfViewerDialog = ({ manual, onClose }) => (_jsxs(Dialog, { open: true, onClose: onClose, maxWidth: false, PaperProps: {
        sx: {
            width: 760,
            maxWidth: '95vw',
            height: '90vh',
            m: 0,
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: '#525659',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
        },
    }, slotProps: { backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)' } } }, children: [_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                minHeight: 44,
                bgcolor: '#323639',
                flexShrink: 0,
                gap: 2,
            }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }, children: [_jsx(DescriptionIcon, { sx: { fontSize: 14, color: colorTokens.gray300, flexShrink: 0 } }), _jsx(Typography, { noWrap: true, sx: { fontSize: 12, color: colorTokens.gray300, fontFamily: 'sans-serif' }, children: manual.title }), manual.version && (_jsxs(Box, { component: "span", sx: {
                                flexShrink: 0,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: '4px',
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: `${colorTokens.primary}55`,
                                color: colorTokens.gray300,
                                fontFamily: 'sans-serif',
                            }, children: ["v", manual.version] }))] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }, children: [_jsx(IconButton, { component: "a", href: manual.url, target: "_blank", rel: "noopener noreferrer", size: "small", title: "Open in new tab", sx: {
                                p: 0.5,
                                borderRadius: 1,
                                color: colorTokens.gray400,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: colorTokens.gray300 },
                            }, children: _jsx(OpenInNewIcon, { sx: { fontSize: 14 } }) }), _jsx(Box, { sx: { width: 1, height: 16, bgcolor: colorTokens.gray500 } }), _jsx(IconButton, { onClick: onClose, size: "small", sx: {
                                p: 0.5,
                                borderRadius: 1,
                                color: colorTokens.gray400,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: colorTokens.gray300 },
                            }, children: _jsx(CloseIcon, { sx: { fontSize: 14 } }) })] })] }), _jsx(Box, { sx: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                p: 1.5,
                bgcolor: '#525659',
                minHeight: 0,
            }, children: _jsx(Box, { component: "iframe", src: `${manual.url}#toolbar=0`, title: manual.title, sx: {
                    flex: 1,
                    width: '100%',
                    border: 'none',
                    borderRadius: '4px',
                    display: 'block',
                } }) })] }));
/* ── Main Component ────────────────────────────────── */
export const ProductManualsTab = () => {
    const { t, i18n } = useTranslation('help');
    const [manuals, setManuals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewManual, setViewManual] = useState(null);
    // Mirror legacy behaviour: ES locale → 'ES', everything else → 'EN'
    const language = i18n.language.toUpperCase() === 'ES' ? 'ES' : 'EN';
    const loadManuals = () => {
        setIsLoading(true);
        setError(null);
        void Promise.all([
            fetchProductManuals({
                manual_type: 'WEB',
                status: 'ACTIVE',
                limit: 1,
                sort: '-released_date',
                language,
            }),
            fetchProductManuals({
                manual_type: 'MOBILE',
                status: 'ACTIVE',
                limit: 1,
                sort: '-released_date',
                language,
            }),
        ])
            .then(([webRes, mobileRes]) => {
            setManuals([...(webRes.data ?? []), ...(mobileRes.data ?? [])]);
        })
            .catch(() => setError('errors.loadManualsFailed'))
            .finally(() => setIsLoading(false));
    };
    useEffect(() => {
        loadManuals();
    }, [language]);
    if (isLoading) {
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 6 }, children: _jsx(CircularProgress, { size: 32, sx: { color: colorTokens.secondary } }) }));
    }
    if (error) {
        return (_jsx(Alert, { severity: "error", children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(Typography, { variant: "body2", children: t(error) }), _jsx(Button, { variant: "outlined", size: "small", onClick: loadManuals, sx: { alignSelf: 'flex-start' }, children: "Retry" })] }) }));
    }
    if (manuals.length === 0) {
        return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }, children: [_jsx(DescriptionIcon, { sx: { fontSize: 48, color: colorTokens.gray300, mb: 1.5 } }), _jsx(Typography, { sx: { fontSize: 13, fontWeight: 600, color: colorTokens.gray600 }, children: "No product manuals available" }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray500, mt: 0.5 }, children: "Check back later for updated documentation" })] }));
    }
    return (_jsxs(_Fragment, { children: [viewManual && _jsx(PdfViewerDialog, { manual: viewManual, onClose: () => setViewManual(null) }), _jsx(Box, { sx: {
                    borderRadius: '16px',
                    bgcolor: 'white',
                    border: `1px solid ${colorTokens.gray200}`,
                    overflow: 'hidden',
                }, children: manuals.map((m, i) => {
                    const type = m.manual_type === 'WEB' ? 'WEB' : 'MOBILE';
                    const meta = MANUAL_META[type];
                    const Icon = meta.Icon;
                    return (_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 2.5,
                            py: 2.5,
                            borderBottom: i < manuals.length - 1 ? `1px solid ${colorTokens.gray200}` : 'none',
                        }, children: [_jsx(Box, { sx: {
                                    width: 48,
                                    height: 48,
                                    borderRadius: '16px',
                                    bgcolor: `${meta.color}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }, children: _jsx(Icon, { sx: { fontSize: 22, color: meta.color } }) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 700, color: colorTokens.textDark, mb: 0.25 }, children: m.title }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600, lineHeight: 1.5, mb: 1 }, children: m.description }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx(Box, { component: "span", sx: {
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: '4px',
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    bgcolor: `${meta.color}12`,
                                                    color: meta.color,
                                                }, children: meta.platformLabel }), _jsxs(Typography, { sx: { fontSize: 10, color: colorTokens.gray600 }, children: [m.version ? `v${m.version} · ` : '', "English"] })] })] }), _jsx(Button, { onClick: () => setViewManual(m), startIcon: _jsx(OpenInNewIcon, { sx: { fontSize: '13px !important' } }), sx: {
                                    textTransform: 'none',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    borderRadius: '10px',
                                    px: 2,
                                    py: 1,
                                    bgcolor: colorTokens.primary,
                                    color: 'white',
                                    flexShrink: 0,
                                    '&:hover': { bgcolor: colorTokens.primaryDark },
                                }, children: "Open" })] }, m.guideId));
                }) }), ' '] }));
};
